import { createFeatureSelector, createSelector } from '@ngrx/store';

import { filter, intersection, isUndefined, lowerCase } from 'lodash-es';
import { DateTime } from 'luxon';

import { WalkingFarmPropertiesOwnerStatusFilterEnum } from '../../enums/walking-farm';
import { WalkingFarmProperty } from '../../typings/walking-farm';
import { State, walkingFarmFeatureKey, walkingFarmSelectEntities } from '../reducers/walking-farm.reducer';

export const selectWalkingFarmFeature = createFeatureSelector<State>(walkingFarmFeatureKey);

export const selectDrawMode = createSelector(selectWalkingFarmFeature, (state: State) => state.drawMode);

export const selectDrawModeType = createSelector(selectWalkingFarmFeature, (state: State) => state.drawModeType);

export const selectDrawFarmRadius = createSelector(selectWalkingFarmFeature, (state: State) => state.drawFarmRadius);

export const selectDrawFarmRadiusInMeters = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.drawFarmRadiusInMeters
);

export const selectDrawFarmCenter = createSelector(selectWalkingFarmFeature, (state: State) => state.drawFarmCenter);

export const selectDrawFarmPoints = createSelector(selectWalkingFarmFeature, (state: State) => state.drawFarmPoints);

export const selectDrawFarmCompleted = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.drawFarmCompleted
);

export const selectKey = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.walkingFarmKey ?? 'BOGUS_FARM_KEY'
);

export const selectFilters = createSelector(selectWalkingFarmFeature, (state: State) => ({
  filterByMaxBathrooms: state.filterByMaxBathrooms,
  filterByMaxBedrooms: state.filterByMaxBedrooms,
  filterByMaxLivingArea: state.filterByMaxLivingArea,
  filterByMinBathrooms: state.filterByMinBathrooms,
  filterByMinBedrooms: state.filterByMinBedrooms,
  filterByMinLivingArea: state.filterByMinLivingArea,
  filterByNoSalesWithinYears: state.filterByNoSalesWithinYears,
  filterByOwnerOccupied: state.filterByOwnerOccupied,
  filterByPropertyType: state.filterByPropertyType,
  filterBySaleTypes: state.filterBySaleTypes
}));

export const selectFilterAvailablePropertyTypes = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.filterAvailablePropertyTypes
);

export const selectFiltersAppliedNumber = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.filtersAppliedNumber
);

export const selectPolygonPoints = createSelector(selectWalkingFarmFeature, (state: State) => state.drawFarmPoints);

export const selectSummary = createSelector(selectWalkingFarmFeature, (state: State) => ({
  errorMessage: state.summaryErrorMessage,
  messages: state.summaryMessages,
  total: state.summaryTotal,
  turnoverRate: state.summaryTurnoverRate
}));

export const selectSavedFarms = createSelector(selectWalkingFarmFeature, (state: State) =>
  filter(state.savedFarms, (item) => item.name.toLowerCase().includes(state.savedFarmsTextFilter.toLowerCase()))
);

export const selectSavedFarmsTextFilter = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.savedFarmsTextFilter
);

export const selectWalkingFarmEntities = createSelector(selectWalkingFarmFeature, walkingFarmSelectEntities);

export const selectWalkingFarm = (id: string) =>
  createSelector(selectWalkingFarmEntities, (walkingFarmEntities) => walkingFarmEntities[id]?.walkingFarm);

export const selectWalkingFarmProperties = (id: string) =>
  createSelector(selectWalkingFarmEntities, (walkingFarmEntities) => {
    const storedEntity = walkingFarmEntities[id];
    let result: WalkingFarmProperty[] = [];
    if (!isUndefined(storedEntity)) {
      let match: RegExpMatchArray | null;
      let saleDate: DateTime;
      let totalYearsInHome: number;
      let ownershipYearsMedian: number;
      let totalPropertiesWithYearsInHome: number;

      result = filter(storedEntity.walkingFarm.properties, (property) => {
        let keep = true;

        switch (storedEntity.filterByOwnerStatus) {
          case WalkingFarmPropertiesOwnerStatusFilterEnum.OwnerOccupied:
            keep = property.A040_OwnerOccupied === 'Y';
            break;
          case WalkingFarmPropertiesOwnerStatusFilterEnum.NonOwnerOccupied:
            keep = property.A040_OwnerOccupied === 'N';
            break;
          case WalkingFarmPropertiesOwnerStatusFilterEnum.OutOfStateOwners:
            match = property.A018_MailAddressCityState?.match(/\s(\w+)$/);
            keep = property.A047_State && match?.length ? property.A047_State !== match[1] : false;
            break;
          case WalkingFarmPropertiesOwnerStatusFilterEnum.EmptyNesters:
            saleDate = DateTime.fromJSDate(new Date(property.A030_SaleDate));
            keep =
              property.A040_OwnerOccupied === 'Y' && saleDate.isValid
                ? DateTime.now().diff(saleDate, 'years').years > 25
                : false;
            break;
          case WalkingFarmPropertiesOwnerStatusFilterEnum.YearsInHome:
            totalPropertiesWithYearsInHome = 0;
            totalYearsInHome = 0;
            saleDate = DateTime.fromJSDate(new Date(property.A030_SaleDate));
            if (saleDate.isValid) {
              totalPropertiesWithYearsInHome++;
              totalYearsInHome += DateTime.now().diff(saleDate, 'years').years;
            }
            ownershipYearsMedian = totalYearsInHome / totalPropertiesWithYearsInHome;
            keep =
              property.A040_OwnerOccupied === 'Y' &&
              DateTime.now().diff(saleDate, 'years').years >= ownershipYearsMedian;
            break;
          default:
            break;
        }
        if (keep && storedEntity.filterByLeadStatus.length) {
          keep = (storedEntity.filterByLeadStatus as string[]).includes(lowerCase(property.Status));
        }
        return keep;
      });
    }
    return result;
  });

export const selectWalkingFarmInAppPurchases = (id: string) =>
  createSelector(
    selectWalkingFarmEntities,
    (walkingFarmEntities) => walkingFarmEntities[id]?.walkingFarm.inAppPurchases
  );

export const selectWalkingFarmViewFiltersAppliedNumber = (id: string) =>
  createSelector(selectWalkingFarmEntities, (walkingFarmEntities) => walkingFarmEntities[id]?.filtersAppliedNumber);

export const selectWalkingFarmBoundariesVisible = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.viewFarmBoundariesVisible
);

export const selectWalkingFarmBoundariesEnabled = (id: string) =>
  createSelector(selectWalkingFarmEntities, (walkingFarmEntities) => walkingFarmEntities[id]?.boundariesEnabled);

export const selectWalkingFarmBoundariesData = (id: string) =>
  createSelector(selectWalkingFarmEntities, (walkingFarmEntities) => walkingFarmEntities[id]?.boundariesData);

export const selectWalkingFarmPhoneAndEmailSelectedPropertyIds = (id: string) =>
  createSelector(
    selectWalkingFarmEntities,
    (walkingFarmEntities) => walkingFarmEntities[id]?.phoneAndEmailSelectedPropertyIds
  );

export const selectMainSelectedTab = createSelector(selectWalkingFarmFeature, (state: State) => state.mainSelectedTab);

export const selectAlertsFarmName = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.farmAlertsFarmName
);

export const selectAlertsEnabled = createSelector(selectWalkingFarmFeature, (state: State) => state.farmAlertsEnabled);

export const selectAlertsAlertLastDate = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.farmAlertsLastAlertDate
);

export const selectAddressSearchText = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.addressSearchText
);

export const selectAddressSearchLocation = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.addressSearchLocation
);

export const selectAlertsList = createSelector(selectWalkingFarmFeature, (state: State) => state.farmAlertsList);

export const selectUnreadAlertsList = createSelector(selectWalkingFarmFeature, (state: State) =>
  filter(state.farmAlertsList, (alert) => !alert.isRead)
);

export const selectAlertsLoaded = createSelector(selectWalkingFarmFeature, (state: State) => state.farmAlertsLoaded);

export const selectPropertyAlertsAlertLastDate = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.propertyFarmAlertsLastAlertDate
);

export const selectPropertyAlertsList = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.propertyFarmAlertsList
);

export const selectUnreadPropertyAlertsList = createSelector(selectWalkingFarmFeature, (state: State) =>
  filter(state.propertyFarmAlertsList, (alert) => !alert.isRead)
);

export const selectPropertyAlertsLoaded = createSelector(
  selectWalkingFarmFeature,
  (state: State) => state.propertyFarmAlertsLoaded
);

export const selectTotalSavedFarms = createSelector(selectWalkingFarmFeature, (state: State) => state.totalSavedFarms);

export const selectWalkingFarmViewFilters = (id: string) =>
  createSelector(selectWalkingFarmEntities, (walkingFarmEntities) => ({
    filterByLeadStatus: walkingFarmEntities[id]?.filterByLeadStatus,
    filterByOwnerStatus: walkingFarmEntities[id]?.filterByOwnerStatus
  }));

export const selectWalkingFarmIsNonDisclosureState = (id: string) =>
  createSelector(
    selectWalkingFarmEntities,
    (walkingFarmEntities) =>
      intersection(
        ['AK', 'ID', 'KS', 'LA', 'MS', 'MO', 'MT', 'NM', 'ND', 'TX', 'UT', 'WY'],
        walkingFarmEntities[id]?.farmStates
      ).length !== 0
  );
